import { useQuery } from '@tanstack/react-query';

export type ConsentScope = {
  scope: string;
  description: string;
};

export type ConsentRequest = {
  pendingId: string;
  clientName: string;
  clientUri: string | null;
  logoUri: string | null;
  policyUri: string | null;
  userEmail: string;
  scopes: ConsentScope[];
};

export const CONSENT_ENDPOINT = '/oauth/consent';

/**
 * The grant the consent screen is asking about.
 *
 * Nothing identifies the request in the URL. The authorization server keys it
 * off a signed, httpOnly cookie, so this page can show what is being approved
 * without ever being in a position to misstate it. A 404 means the request
 * expired or was already answered, which the screen handles as its own state
 * rather than an error.
 */
export const useConsentRequest = () =>
  useQuery({
    queryKey: ['oauth-consent-request'],
    retry: false,
    // A grant is approved once. Re-reading a spent one on a window focus would
    // only ever replace the screen with an expiry notice.
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<ConsentRequest | null> => {
      const response = await fetch(CONSENT_ENDPOINT, {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });

      if (response.status === 404 || response.status === 401) {
        return null;
      }
      if (!response.ok) {
        throw new Error(`Consent request failed: ${response.status}`);
      }

      return response.json();
    },
  });
